// client/src/components/CourseCard.js

import React, { useState, useEffect } from 'react';
import { Card, Button, Col, Image, Row, Placeholder } from 'react-bootstrap';
import { FaUser } from 'react-icons/fa';
import { fetchTeeTimesForCourse } from '../services/api';

// --- Helper Functions ---

const formatFilterTime = (hour) => `${String(hour).padStart(2, '0')}:00`;

const convertTo24Hour = (timeString) => {
  if (!timeString || !timeString.includes(' ')) return '00:00';
  const [time, modifier] = timeString.split(' ');
  let [hours, minutes] = time.split(':');
  if (hours === '12') hours = '00';
  if (modifier.toLowerCase() === 'pm') hours = parseInt(hours, 10) + 12;
  return `${String(hours).padStart(2, '0')}:${minutes}`;
};

const getLowestPrice = (teeTimes, filters) => {
  if (!teeTimes || teeTimes.length === 0) return null;

  // Determine which price to look for based on the "holes" filter
  const priceKey = filters.holes === 9 ? '9_holes' : '18_holes';

  const validPrices = teeTimes
    .map(t => {
      // Safely access the nested price object and get the 'walking' price
      const priceObject = t.price?.[priceKey];
      return priceObject ? parseFloat(priceObject.walking) : null;
    })
    .filter(p => p !== null && !isNaN(p) && p > 0); // Filter out nulls or invalid numbers

  if (validPrices.length === 0) return null;
  return Math.min(...validPrices);
};

// Helper to get a course-specific local image
const getLocalCourseImage = (courseName) => {
  // Guard against a null/undefined name, which can happen during rendering
  if (!courseName) return;

  const trimmedName = courseName.trim(); // Removes any hidden whitespace

  const imageMap = {
    'Lions Municipal Golf Course': '/images/Lions.jpg',
    'Roy Kizer Golf Course': '/images/Kizer.jpg',
    'Jimmy Clay Golf Course': '/images/Clay.jpg',
    'Morris Williams Golf Course': '/images/Morris-Williams.jpg',
  };

  return imageMap[trimmedName];
};

// --- Skeleton Component for Loading State ---

const CourseCardSkeleton = () => (
  <Col md={12}>
    <Card className="shadow-sm w-100 p-3 h-100">
      <Card.Body>
        <div className="d-flex align-items-center mb-3">
          <Placeholder as="div" animation="glow" style={{ width: '60px', height: '60px', borderRadius: '8px' }}>
            <Placeholder xs={12} style={{ height: '100%' }} />
          </Placeholder>
          <div className="ms-3 flex-grow-1">
            <Placeholder as={Card.Title} animation="glow">
              <Placeholder xs={8} />
            </Placeholder>
            <Placeholder as={Card.Subtitle} animation="glow">
              <Placeholder xs={5} />
            </Placeholder>
          </div>
        </div>
        <Placeholder as="p" animation="glow"><Placeholder xs={4} /></Placeholder>
        <Row className="g-2 text-center">
          {[...Array(6)].map((_, i) => (
            <Col xs={4} key={i}>
              <Placeholder.Button variant="secondary" xs={12} style={{ height: '40px' }} />
            </Col>
          ))}
        </Row>
      </Card.Body>
    </Card>
  </Col>
);


// --- Main CourseCard Component ---
const CourseCard = ({ course, date, filters, onTitleClick, onTimeClick, onScheduleClick, onStandbyClick }) => {
  const [allTeeTimes, setAllTeeTimes] = useState([]);
  const [filteredTeeTimes, setFilteredTeeTimes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Whether this course's booking window is open for the searched date, per its own
  // booking_classes rules -- reported by the server independent of whether the live tee-time
  // scrape itself succeeded, so a Cloudflare-blocked scrape doesn't masquerade as "not open yet".
  const [isWindowOpen, setIsWindowOpen] = useState(false);

  useEffect(() => {
    const loadTeeTimes = async () => {
      if (!course.id || !date) return;
      setIsLoading(true);
      setError(null);
      try {
        const result = await fetchTeeTimesForCourse({ courseId: course.id, date });
        setAllTeeTimes(result.teeTimes || []);
        setIsWindowOpen(!!result.isWindowOpen);
      } catch (err) {
        setError('Server error. Try refreshing in a couple minutes.');
        setIsWindowOpen(!!err.response?.data?.isWindowOpen);
      } finally {
        setIsLoading(false);
      }
    };
    loadTeeTimes();
  }, [course.id, date]);

  useEffect(() => {
    if (allTeeTimes.length === 0) {
      setFilteredTeeTimes([]);
      return;
    }
    const startTime = formatFilterTime(filters.startTime);
    const endTime = formatFilterTime(filters.endTime);
    const filtered = allTeeTimes.filter(time => {
      const teeTime24 = convertTo24Hour(time.time);
      const isAfterStart = teeTime24 >= startTime;
      const isBeforeEnd = teeTime24 <= endTime;
      const hasEnoughSlots = parseInt(time.openSlots) >= filters.golfers;
      return isAfterStart && isBeforeEnd && hasEnoughSlots;
    });
    setFilteredTeeTimes(filtered);
  }, [allTeeTimes, filters]);

  const maxVisibleTimes = 6;
  const visibleTimes = filteredTeeTimes.slice(0, maxVisibleTimes);

  // ✅ Pass the filters object to the helper
  const lowestPrice = getLowestPrice(filteredTeeTimes, filters);

  if (isLoading) {
    return <CourseCardSkeleton />;
  }

  return (
    <Col md={12} className="h-100">
      {/* ✅ Add h-100 to make the card fill the vertical space of the row */}
      <Card className="shadow-sm w-100 p-3 h-100">
        <Card.Body className="d-flex flex-column"> {/* Use flex-column for alignment */}
          {/* Card Header */}
          <div className="d-flex align-items-center mb-3">
            {(() => {
              const localImage = getLocalCourseImage(course.name);
              const imageUrl = course.image_url || localImage || '/images/golf-flag.png';

              return (
                <Image
                  src={imageUrl}
                  alt={course.name}
                  className="rounded"
                  style={{ width: '60px', height: '60px', objectFit: 'cover', border: '1px solid #eee' }}
                />
              );
            })()}
            <div className="ms-3 flex-grow-1">
              <h5 className="fw-bold mb-1" style={{ cursor: 'pointer' }} onClick={() => onTitleClick?.(course, allTeeTimes)}>
                {course.name}
              </h5>
              <p className="text-muted mb-0">{course.course_type} • {course.city}</p>
            </div>
          </div>

          {/* Price Display */}
          <div className="mb-3">
            {lowestPrice ? (
              <div>
                <span className="fw-bold display-6 text-dark">${lowestPrice.toFixed(0)}</span>
                <span className="text-muted ms-1">per golfer</span>
              </div>
            ) : (
              <div style={{ height: '56px' }} /> // Placeholder to maintain height
            )}
          </div>

          {/* Tee Times Grid */}
          <div style={{ minHeight: '98px' }}>
            {/* State 1: An error occurred (uses your new message) */}
            {error && (
              <div className="text-center text-danger small pt-3">
                <p className="mb-0">{error}</p>
              </div>
            )}

            {/* State 2: No error, but no tee times were found for the day */}
            {!error && allTeeTimes.length === 0 && (
              <div className="text-center text-muted pt-3">
                <p>No tee times available for this day.</p>
              </div>
            )}

            {/* State 3: Times were found, but they are outside the user's filters */}
            {!error && allTeeTimes.length > 0 && filteredTeeTimes.length === 0 && (
              <div className="text-center text-muted pt-3">
                <p>No times available for your selected filters.</p>
              </div>
            )}

            {/* State 4: Times are available and match the filters */}
            {!error && filteredTeeTimes.length > 0 && (
              <Row className="g-2 text-center">
                {visibleTimes.map((time, index) => {
                  const timeParts = time.time.split(' ');
                  const timeValue = timeParts[0];
                  const timePeriod = timeParts[1];

                  return (
                    <Col xs={4} key={index}>
                      <Button
                        size="sm"
                        variant={time.canBook ? "light" : "outline-secondary"}
                        className="w-100 py-1 px-2 shadow-sm d-flex flex-column rounded-pill"
                        disabled={!time.canBook}
                        onClick={() => onTimeClick(course, time)}
                        style={{ lineHeight: '1.2' }}
                      >
                        <span className="fw-bold" style={{ whiteSpace: 'nowrap' }}>
                          {timeValue}
                          {timePeriod && <small className="ms-1 fw-normal">{timePeriod}</small>}
                        </span>

                        {parseInt(time.openSlots, 10) > 0 && (
                          <small className="text-muted d-flex align-items-center justify-content-center">
                            {time.openSlots} <FaUser className="ms-1" />
                          </small>
                        )}
                      </Button>
                    </Col>
                  );
                })}

                {filteredTeeTimes.length > maxVisibleTimes && (
                  <Col xs={12} className="mt-3 text-center">
                    <Button
                      variant="link"
                      className="text-primary text-decoration-none"
                      onClick={() => onTitleClick?.(course, allTeeTimes)}
                    >
                      See all {filteredTeeTimes.length} available times
                    </Button>
                  </Col>
                )}
              </Row>
            )}
          </div>

          {/* Footer Action Button — always available, regardless of whether the live tee-time
              search succeeded, failed, or came back empty. Standby/advance-booking requests
              are just a DB row the cron scans for later; they don't depend on live inventory
              data at all, so a scrape failure shouldn't block setting one up. isWindowOpen comes
              from the server's own booking_classes schedule for this course/date, not from
              whether live tee times were found — so a Cloudflare-blocked scrape still correctly
              offers "Join Standby List" when the window is actually open. */}
          <div className="mt-auto">
            <hr className="my-3" />
            <div className="text-center">
              <Button
                variant="primary"
                className="w-100"
                onClick={() => isWindowOpen ? onStandbyClick(course) : onScheduleClick(course)}
              >
                {isWindowOpen ? 'Join Standby List' : 'Schedule for Future Date'}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default CourseCard;